"use client";

import { cn } from "@/lib/utils";

export type AchievementVariant = "amber" | "blue" | "emerald" | "purple" | "rose" | "default";

export interface AchievementCardProps {
  /** Color variant */
  variant?: AchievementVariant;
  /** Additional CSS classes */
  className?: string;
  /** Children content */
  children: React.ReactNode;
}

const variantClasses: Record<AchievementVariant, string> = {
  amber: "bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-950/30 dark:to-amber-950/30 border-amber-200 dark:border-amber-800",
  blue: "bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border-blue-200 dark:border-blue-800",
  emerald: "bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border-emerald-200 dark:border-emerald-800",
  purple: "bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-950/30 dark:to-violet-950/30 border-purple-200 dark:border-purple-800",
  rose: "bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-950/30 dark:to-pink-950/30 border-rose-200 dark:border-rose-800",
  default: "bg-card border-border",
};

const textColorClasses: Record<AchievementVariant, string> = {
  amber: "text-amber-600 dark:text-amber-400",
  blue: "text-blue-600 dark:text-blue-400",
  emerald: "text-emerald-600 dark:text-emerald-400",
  purple: "text-purple-600 dark:text-purple-400",
  rose: "text-rose-600 dark:text-rose-400",
  default: "text-foreground",
};

/**
 * AchievementCard - Gradient card for displaying achievements/stats
 *
 * Use for displaying achievements, milestones, or highlighted statistics
 * with a colored gradient background.
 *
 * @example
 * <AchievementCard variant="amber">
 *   <IconCircle icon={Trophy} variant="amber" size="lg" className="mx-auto mb-2" />
 *   <h3 className="font-semibold mb-1">Birdies</h3>
 *   <p className="text-2xl font-bold">42</p>
 * </AchievementCard>
 */
export function AchievementCard({
  variant = "default",
  className,
  children,
}: AchievementCardProps) {
  return (
    <div
      className={cn(
        "text-center p-4 rounded-xl border transition-all duration-200 hover:shadow-md",
        variantClasses[variant],
        className
      )}
    >
      {children}
    </div>
  );
}

/**
 * Get the appropriate text color class for a variant
 */
export function getAchievementTextColor(variant: AchievementVariant): string {
  return textColorClasses[variant];
}

export default AchievementCard;

